/**
 * KISSED University · Student "My Enrollments" view.
 * Route: /university/my-enrollments
 *
 * Email-keyed lookup until SSO ships. Displays all enrollments for the email,
 * with tuition tracking and payment history (purchased_courses) for PPC plans.
 */
import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Search, Receipt, GraduationCap, BookOpen, FileText, Stethoscope, CreditCard, ExternalLink } from "lucide-react";
import logo from "@/assets/brand/kissed-logo.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { universityApi } from "@/lib/universityApi";
import { formatApiError } from "@/lib/formatError";

const STATUS_TONE = {
  started: "bg-slate-100 text-slate-700",
  pending_payment: "bg-amber-100 text-amber-800",
  pending: "bg-purple-100 text-purple-800",
  active: "bg-emerald-100 text-emerald-800",
  on_hold: "bg-yellow-100 text-yellow-800",
  graduated: "bg-emerald-200 text-emerald-900",
  withdrawn: "bg-slate-200 text-slate-700",
  expelled: "bg-red-100 text-red-800",
};
const STATUS_LABEL = {
  started: "Started",
  pending_payment: "Pending payment",
  pending: "Pending review",
  active: "Active",
  on_hold: "On hold",
  graduated: "Graduated",
  withdrawn: "Withdrawn",
  expelled: "Expelled",
};
const TYPE_ICON = {
  certificate: BookOpen,
  residency: Stethoscope,
  degree: GraduationCap,
  continuing_education: FileText,
};

const usd = (n) =>
  n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export default function UniversityMyEnrollments() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [searched, setSearched] = useState(false);
  const fresh = params.get("fresh");

  const lookup = async (e) => {
    if (e) e.preventDefault();
    if (!email.trim()) {
      toast.error("Enter the email you used at enrollment.");
      return;
    }
    setLoading(true);
    try {
      const r = await universityApi.enrollmentByEmail(email.trim());
      setItems(r.data?.items || []);
      setSearched(true);
    } catch (err) {
      toast.error(formatApiError(err, "Lookup failed"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (email) {
      setTimeout(() => lookup(), 0);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="university-my-enrollments" className="min-h-screen bg-[#faf7f5]">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/university"><img src={logo} alt="KISSED" className="h-11 w-auto drop-shadow-md" /></Link>
          <span className="font-display text-sm text-slate-700">My enrollments</span>
          <div className="ml-auto">
            <Link to="/university/enroll" data-testid="link-enroll" className="text-sm text-purple-900 hover:underline">
              Enroll in a program →
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-6">
        <ResumeEnrollmentBanner />
        {fresh && (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="my-enr-fresh-banner">
            Your enrollment has been created. Below is your tuition tracker and payment history.
          </div>
        )}

        <form onSubmit={lookup} className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3">
          <h1 className="font-display text-xl text-slate-900">Find my enrollments</h1>
          <p className="text-sm text-slate-600">Enter the email you used when you enrolled to see all your active and past program enrollments.</p>
          <div>
            <Label>Email</Label>
            <Input data-testid="my-enr-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2" />
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={loading} data-testid="my-enr-lookup-btn" className="bg-purple-900 hover:bg-purple-800">
              {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}Look up
            </Button>
          </div>
        </form>

        {searched && items.length === 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600 text-center" data-testid="my-enr-empty">
            We didn&apos;t find any enrollments for that email.
            <div className="mt-2">
              <Link to="/university/enroll" className="text-purple-900 hover:underline">Browse programs →</Link>
            </div>
          </div>
        )}

        {items.map((e) => {
          const Icon = TYPE_ICON[e.program_type] || BookOpen;
          const courses = e.purchased_courses || [];
          const showPayCta = e.status === "pending_payment" && e.tuition_balance_usd > 0;
          return (
            <div key={e.id} className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3" data-testid={`my-enr-card-${e.public_enrollment_id || e.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-50 text-purple-900 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-wide text-purple-900 font-medium">
                    {(e.program_type || "").replace("_", " ")}
                  </div>
                  <div className="font-display text-base text-slate-900">{e.program_name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {e.public_enrollment_id ? <span className="font-mono">{e.public_enrollment_id}</span> : null}
                    {e.started_at ? <> · started {new Date(e.started_at).toLocaleDateString()}</> : null}
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${STATUS_TONE[e.status] || "bg-slate-100 text-slate-700"}`} data-testid={`my-enr-status-${e.id}`}>
                  {STATUS_LABEL[e.status] || e.status}
                </span>
              </div>

              {showPayCta && <PayNowCta enrollment={e} />}

              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                  <div className="text-[11px] uppercase tracking-wide text-slate-500">Plan</div>
                  <div className="font-medium text-slate-900 mt-0.5">
                    {e.payment_plan === "pay_in_full" ? "Pay In Full" : e.payment_plan === "pay_per_course" ? "Pay Per Course" : "Undecided"}
                  </div>
                </div>
                <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                  <div className="text-[11px] uppercase tracking-wide text-slate-500">Paid</div>
                  <div className="font-medium text-slate-900 mt-0.5">{usd(e.tuition_paid_usd)}</div>
                </div>
                <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                  <div className="text-[11px] uppercase tracking-wide text-slate-500">Balance</div>
                  <div className="font-medium text-slate-900 mt-0.5">{usd(e.tuition_balance_usd)}</div>
                </div>
              </div>

              {courses.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500 mt-2 mb-1.5 flex items-center gap-1.5">
                    <Receipt className="w-3.5 h-3.5" /> Payment history
                  </div>
                  <div className="rounded-xl border border-slate-100 divide-y divide-slate-100">
                    {courses.map((c, idx) => (
                      <div key={idx} className="px-3 py-2 text-sm flex items-center justify-between" data-testid={`my-enr-course-${e.id}-${idx}`}>
                        <div>
                          <div className="font-medium text-slate-900">{c.course_name || c.course_code}</div>
                          <div className="text-[11px] text-slate-500 font-mono">
                            {c.course_code}{c.payment_reference ? ` · ${c.payment_reference}` : ""}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-slate-900 font-medium">{usd(c.amount_paid_usd)}</div>
                          <div className="text-[11px] text-slate-500">
                            {c.purchased_at ? new Date(c.purchased_at).toLocaleDateString() : ""}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </main>
    </div>
  );
}

function ResumeEnrollmentBanner() {
  const [draft, setDraft] = useState(null);
  useEffect(() => {
    try { setDraft(JSON.parse(localStorage.getItem("kcis.enroll.draft.v1") || "null")); }
    catch { setDraft(null); }
  }, []);
  if (!draft?.selected) return null;
  return (
    <div className="rounded-2xl border border-purple-200 bg-purple-50/70 p-4 text-sm text-purple-900 flex items-center justify-between" data-testid="resume-enrollment-card">
      <div>
        <div className="font-medium">You have an enrollment in progress</div>
        <div className="text-xs mt-0.5">{draft.selected}</div>
      </div>
      <Link to="/university/enroll" className="rounded-lg bg-purple-900 hover:bg-purple-800 text-white text-sm font-medium px-3 py-1.5" data-testid="resume-enrollment-btn">
        Resume Enrollment
      </Link>
    </div>
  );
}

function PayNowCta({ enrollment }) {
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await universityApi.enrollmentTuitionCheckout(enrollment.id);
      setInfo(r.data);
    } catch (err) {
      toast.error(formatApiError(err, "Failed to load checkout"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-3 text-sm" data-testid={`my-enr-pay-cta-${enrollment.id}`}>
      <div className="flex items-start gap-2">
        <CreditCard className="w-4 h-4 mt-0.5 text-amber-800 shrink-0" />
        <div className="flex-1">
          <div className="font-medium text-amber-900">Tuition payment required to activate enrollment</div>
          <div className="text-xs text-amber-900 mt-0.5">
            Balance due: <span className="font-medium">${Number(enrollment.tuition_balance_usd || 0).toLocaleString()}</span>.
            Pay via Square — your enrollment will be activated by admissions within 1 business day.
          </div>
          {loading && <div className="text-xs text-amber-800 mt-2"><Loader2 className="w-3 h-3 inline animate-spin mr-1" />Loading checkout…</div>}
          {info && info.checkout_configured && (
            <a
              href={info.checkout_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`my-enr-pay-btn-${enrollment.id}`}
              className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium px-3 py-1.5"
            >
              Pay Tuition Now <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
          {info && !info.checkout_configured && (
            <div className="mt-2 text-xs text-amber-900" data-testid={`my-enr-pay-fallback-${enrollment.id}`}>
              Square checkout is not yet wired for this program. Please email
              <a href="mailto:admissions@kissedbycvon.com" className="underline ml-1">admissions@kissedbycvon.com</a> and we will
              send a secure payment link directly.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
